// External dependencies
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

// Internal dependencies
import { getCircleBackgroundStyle } from '@/modules/tribes/client/utils';
import { getRouteParams } from '@/modules/core/client/services/angular-compat';
import { userType } from '@/modules/users/client/users.prop-types';
import * as circlesAPI from '@/modules/tribes/client/api/tribes.api';
import Board from '@/modules/core/client/components/Board.js';
import FooterMenu from '@/modules/cms/client/components/FooterMenu.component';
import BoardCredits from '@/modules/core/client/components/BoardCredits.js';
import HomeSearch from './HomeSearch.component.js';
import ManifestoText from './ManifestoText.component.js';
// import Screenshot from '@/modules/core/client/components/Screenshot.js';
import VideoFrame from '@/modules/core/client/components/VideoFrame.js';
// import screenshotProfilePng from '../img/screenshot-profile.png';
// import screenshotProfilePng2x from '../img/screenshot-profile-2x.png';
// import screenshotProfileWebp from '../img/screenshot-profile.webp';
// import screenshotProfileWebp2x from '../img/screenshot-profile-2x.webp';
// import screenshotSearchPng from '../img/screenshot-search.png';
// import screenshotSearchPng2x from '../img/screenshot-search-2x.png';
// import screenshotSearchWebp from '../img/screenshot-search.webp';
// import screenshotSearchWebp2x from '../img/screenshot-search-2x.webp';
// import HostFolk from '../../../../public/HostFolk.mp4';

/**
 * List of photos to randomly pick as cover photo for homepage
 *
 * @param  {[String]} circleSlug Slug of circle.
 * @return {Array}
 */
function getBoardPictures(circleSlug) {
  // Default photos
  let boards = [
    'woman-bridge',
    'rainbowpeople',
    'hitchroad',
    'hitchgirl1',
    'wavewatching',
    'sahara-backpacker',
    'hitchtruck',
  ];

  // Different set of photos for cyclists circle
  if (circleSlug === 'cyclists') {
    boards = ['cyclist'];
  }

  // Different set of photos for these 3 circles
  if (
    circleSlug &&
    ['hitchhikers', 'dumpster-divers', 'punks'].includes(circleSlug)
  ) {
    boards = [
      'rainbowpeople',
      'hitchroad',
      'desertgirl',
      'hitchgirl1',
      'hitchgirl2',
      'hitchtruck',
    ];
  }

  return boards;
}

/**
 * Signup URL appended with circle if available
 *
 * @param  {[String]} circleSlug Slug of circle.
 * @return {String} Signup URL
 */
export function getSignupUrl(circleSlug) {
  if (circleSlug) {
    // @TODO: change `tribe` to `circle`, needs changes in Signup form controller
    return `/signup?tribe=${circleSlug}`;
  }

  return '/signup';
}

export default function Home({ user, photoCredits }) {
  const { t } = useTranslation('pages');
  // `tribe` route supported for legacy reasons, deprecated Feb 2021
  const { circle: circleRouteParam, tribe: tribeRouteParam } = getRouteParams();
  const circleRoute = circleRouteParam || tribeRouteParam;

  // @TODO change this to be based on UI language rather than browser locale
  // const memberCount = new Intl.NumberFormat().format(120000);

  // TODO get header height instead of magic number 56
  // const headerHeight = angular.element('#tr-header').height() || 0; // code of the original angular controller
  const headerHeight = 56;

  const boardHeight =
    window.innerWidth <= 480 && window.innerHeight < 700
      ? 400
      : window.innerHeight - headerHeight + 14;

  // Wide screens get a fixed-height intro with the content centred in it.
  // On phones the search form stacks and can outgrow the viewport, so the
  // board is allowed to grow instead of scrolling inside itself.
  const boardStyle =
    window.innerWidth < 768
      ? { minHeight: boardHeight }
      : { height: boardHeight };

  const boards = getBoardPictures(circleRoute);

  const [circles, setCircles] = useState([]);

  useEffect(() => {
    async function fetchData() {
      const circles = await circlesAPI.read({ limit: 3 });
      const circleIsLoaded = circles.some(t => t.slug === circleRoute);

      if (circleRoute && !circleIsLoaded) {
        const extraCircle = await circlesAPI.get(circleRoute);

        if (extraCircle && extraCircle._id) {
          circles.unshift(extraCircle);
        }
      }
      setCircles(circles);
    }
    fetchData();
  }, []);

  return (
    <>
      {!user && (
        // TODO apply tr-boards-ignore-small attribute here and implement functionality in the Board.js controller
        <Board
          className="board-primary container home-intro"
          names={boards}
          style={boardStyle}
        >
          <div className="middle-wrapper middle-wrapper-horizontal">
            <div className="middle-content">
              <div className="row">
                <div className="col-xs-12 col-sm-8 col-sm-offset-2">
                  <img
                    className="home-logo hidden-xs center-block"
                    src="/img/logo/white.svg"
                    alt="Hostfolk"
                    width="210"
                    height="210"
                    aria-hidden="true"
                  />
                  <img
                    className="home-logo visible-xs-block center-block"
                    src="/img/logo/white.svg"
                    alt="Hostfolk"
                    width="130"
                    height="130"
                    aria-hidden="true"
                  />
                  <h1 className="sr-only">Hostfolk</h1>
                  <h3 className="home-tagline">
                    {t('Home is where cultures meet')}
                  </h3>
                  <h4 className="home-subtagline">
                    {t(
                      'A warm, inclusive message about shared living and learning.',
                    )}
                  </h4>
                  <HomeSearch user={user} />
                  <a
                    href={getSignupUrl(circleRoute)}
                    className="btn btn-action btn-default home-join hidden-xs"
                  >
                    {t('Join Hostfolk now')}
                  </a>
                  {/*
                  {!isNativeMobileApp && (
                    <div className="home-apps">
                      <a
                        href="https://play.google.com/store/apps/details?id=org.trustroots.trapp"
                        rel="noopener"
                        className="btn btn-lg btn-default"
                      >
                        <i className="icon-android"></i>
                        {t('Play Store')}
                      </a>
                      <a
                        href="https://apps.apple.com/nl/app/trustroots-community-app/id6447916847"
                        rel="noopener"
                        className="btn btn-lg btn-default"
                      >
                        <i className="icon-apple"></i>
                        {t('App Store')}
                      </a>
                    </div>
                  )}
                  */}
                  <div className="home-down hidden-xs">
                    <i className="icon-down"></i>
                  </div>
                </div>
              </div>
              {/* .row */}
            </div>
          </div>
        </Board>
      )}

      {/* Members get the search bar on a compact banner */}
      {user && (
        <Board
          className="board-primary container home-intro home-intro-compact"
          names={boards}
        >
          <div className="row">
            <div className="col-xs-12 col-md-10 col-md-offset-1">
              <h2 className="home-tagline">
                {t('Find your home away from home')}
              </h2>
              <HomeSearch user={user} />
            </div>
          </div>
        </Board>
      )}

      {/* How does it work */}
      <section className="home-how">
        <div className="container">
          <div className="row">
            <div className="col-md-12 text-center lead">
              <div className="icon-home icon-4x text-muted"></div>
              <h2 className="font-brand-semibold">{t('How it works')}</h2>
              <br />
              <VideoFrame src="/HostFolk.mp4" />
              <br />
              <div className="row">
                <div className="col-xs-12 col-sm-offset-2 col-sm-6 col-md-offset-0 col-md-6">
                  <h3 className="font-brand-regular">{t('For Hosts')}</h3>
                  <br />
                  <p className="font-brand-light">
                    <ul className="text-left">
                      <li>{t(`SignUp at hostfolk.com`)}</li>
                      <li>
                        {t(
                          `Create a Profile: Share details about your home, location, and what kind of guests you’d like to welcome.`,
                        )}
                      </li>
                      <li>
                        {t(
                          `Connect with Travellers: Browse profiles of students and Travellers looking for a homestay.`,
                        )}
                      </li>
                      <li>
                        {t(
                          `Host and Earn: Offer a warm, welcoming space and earn extra income while sharing your culture.`,
                        )}
                      </li>
                    </ul>
                    {/* @TODO remove ns (issue #1368) */}
                    {/* <Trans t={t} ns="pages" values={{ memberCount }}>
                  Hostfolk is over{' '}
                  <a href="/statistics">{{ memberCount }} members</a> strong and
                  growing!
                </Trans> */}
                  </p>
                </div>
                <div className="col-xs-12 col-sm-offset-2 col-sm-6 col-md-offset-0 col-md-6">
                  <h3 className="font-brand-regular">
                    {t('For Students & Travellers')}
                  </h3>
                  <br />
                  <p className="font-brand-light">
                    <ul className="text-left">
                      <li>{t(`SignUp at hostfolk.com`)}</li>
                      <li>
                        {t(
                          `Search for Hosts: Find hosts that match your travel plans and interests.`,
                        )}
                      </li>
                      <li>
                        {t(
                          `Book Your Stay: Arrange your home stay directly through the platform.`,
                        )}
                      </li>
                      <li>
                        {t(
                          `Live the Experience: Immerse yourself in local culture while enjoying the comfort of a real home.`,
                        )}
                      </li>
                    </ul>
                    {/* @TODO remove ns (issue #1368) */}
                    {/* <Trans t={t} ns="pages" values={{ memberCount }}>
                  Hostfolk is over{' '}
                  <a href="/statistics">{{ memberCount }} members</a> strong and
                  growing!
                </Trans> */}
                  </p>
                </div>
              </div>
            </div>
            {/* <div aria-hidden className="col-md-7"> */}
            {/* <Screenshot
                png={screenshotSearchPng}
                png2x={screenshotSearchPng2x}
                webp={screenshotSearchWebp}
                webp2x={screenshotSearchWebp2x}
              /> */}
            {/* </div> */}
          </div>
        </div>
      </section>

      {/* Share */}
      <section className="home-how">
        <div className="container">
          <div className="row">
            {/* <div aria-hidden className="col-xs-12 col-md-7 hidden-xs hidden-sm">
              <Screenshot
                png={screenshotProfilePng}
                png2x={screenshotProfilePng2x}
                webp={screenshotProfileWebp}
                webp2x={screenshotProfileWebp2x}
              />
            </div> */}
            <div className="col-xs-12 col-sm-offset-2 col-sm-12 col-md-offset-0 col-md-12 text-center lead">
              <div className="home-wohoo center-block hidden-xs hidden-sm"></div>
              <p className="font-brand-light">
                <br className="hidden-xs hidden-sm" />
                <h2 className="font-brand-semibold">{t('Why HostFolk?')}</h2>
                <br />
                {/* {t('Share the beautiful you with the world.')} */}
                <div className="row">
                  <div className="col-xs-12 col-sm-offset-2 col-sm-6 col-md-offset-0 col-md-6">
                    <h3 className="font-brand-regular">{t('For Hosts')}</h3>
                    <br />
                    <ul className="text-left">
                      <li>
                        {t(
                          `Cultural Enrichment: Hosting international students lets you experience new languages, customs, and perspectives—right from your living room.`,
                        )}
                      </li>
                      <li>
                        {t(
                          `Extra Income: Earn money by offering a spare room, with minimal added costs. Many hosts find it’s a rewarding way to supplement their income.`,
                        )}
                      </li>
                      <li>
                        {t(
                          `Lifelong Connections: Build friendships that span continents. Many hosts stay in touch with their guests long after the stay ends.`,
                        )}
                      </li>
                      <li>
                        {t(
                          `Family Benefits: Your children gain exposure to global cultures and often form lasting friendships with guests their age.`,
                        )}
                      </li>
                    </ul>
                  </div>
                  <div className="col-xs-12 col-sm-offset-2 col-sm-6 col-md-offset-0 col-md-6">
                    <h3 className="font-brand-regular">
                      {t('For Students & Travellers')}
                    </h3>
                    <br />
                    <ul className="text-left">
                      <li>
                        {t(
                          `Authentic Experience: Skip the tourist traps—live with locals and immerse yourself in real culture.`,
                        )}
                      </li>
                      <li>
                        {t(
                          `Supportive Environment: Hosts provide more than a bed—they offer guidance, meals, and a sense of belonging.`,
                        )}
                      </li>
                      <li>
                        {t(
                          `Language Immersion: Practice your language skills daily in a natural setting.`,
                        )}
                      </li>
                      <li>
                        {t(
                          `Affordable Stays: Homestays are often more budget-friendly than hotels or rentals.`,
                        )}
                      </li>
                    </ul>
                  </div>
                </div>
                {/* {t('Be genuine yourself.')} */}
              </p>
            </div>
          </div>
        </div>
      </section>

      {circles.length > 0 && (
        <section className="home-how">
          <div className="container">
            <div className="row">
              <div className="col-xs-10 col-xs-push-1 col-sm-3 col-sm-push-6">
                <div className="text-center tribe-intro">
                  <h2 className="font-brand-light">{t('Groups')}</h2>
                  <p className="font-brand-light">
                    {t(
                      "Joining groups helps you find likeminded Trustroots members and\n                  tells others what you're interested in.",
                    )}
                    <br />
                    <br />
                    <a href="/circles" className="btn btn-default btn-lg">
                      {t('More Groups')}
                    </a>
                  </p>
                </div>
              </div>
              <div className="col-xs-12 visible-xs tribes-xs">
                {circles.slice(0, 3).map(circle => (
                  <a
                    key={circle._id}
                    href={`/circles/${circle.slug}`}
                    className="img-circle tribe-xs tribe-image"
                    style={getCircleBackgroundStyle(circle, '742x496')}
                  >
                    {!circle.image && <span>{circle.label.charAt(0)}</span>}
                  </a>
                ))}
              </div>
              {circles.slice(0, 3).map((circle, index, items) => (
                <div
                  key={circle._id}
                  className={classnames('col-sm-3', 'hidden-xs', {
                    'col-sm-pull-3': index < items.length - 1,
                  })}
                >
                  <div
                    className="img-circle tribe tribe-image"
                    style={getCircleBackgroundStyle(circle, '742x496')}
                  >
                    <a href={`/circles/${circle.slug}`} className="tribe-link">
                      <h3 className="tribe-label">{circle.label}</h3>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Manifesto */}
      <Board
        className="board-primary board-inset"
        names="mountainforest"
        id="manifesto"
      >
        <div className="container">
          <div className="row">
            <div className="col-md-offset-3 col-md-6 text-center lead font-brand-light">
              <ManifestoText></ManifestoText>
              {!user && (
                <p>
                  <br />
                  <br />
                  <a
                    href={getSignupUrl(circleRoute)}
                    className="btn btn-lg btn-action btn-default"
                  >
                    {t('Join Hostfolk')}
                  </a>
                </p>
              )}
            </div>
          </div>
        </div>
      </Board>

      {/* Footer */}
      <Board
        className="board-primary board-inset home-footer"
        names="bokeh"
        ignoreBackgroundOnSmallScreen
      >
        <div className="container">
          <div className="row">
            <div className="col-sm-6 col-md-3">
              <h3 className="font-brand-light">{t('About HostFolk')}</h3>
              <p>
                {t(
                  `HostFolk is a homestay platform built around the idea that hospitality can be transformative. It connects international exchange students, independent travellers, and host families through meaningful Homestay experiences, fostering cultural exchange, friendship, and personal growth.`,
                )}
              </p>
              <p>
                <i className="icon-right"></i>
                <a
                  href="/foundation"
                  className="home-footer-more font-brand-semibold"
                >
                  {t('Learn more')}
                </a>
              </p>
            </div>
            {/* Company / Explore / Customer Service are managed from
                Admin → CMS → Footer menu. The original columns are the
                fallback, shown until the CMS menu loads or if it is empty. */}
            <FooterMenu
              columnClassName="col-sm-6 col-md-3"
              fallback={
                <>
                  <div className="col-sm-6 col-md-3">
                    <h3 className="font-brand-light">{t('Company')}</h3>
                    <ul className="list-unstyled home-footer-pages font-brand-light">
                      <li>
                        <a href="/foundation">{t('About HostFolk')}</a>
                      </li>
                      <li>
                        <a href="/rules">{t('Terms and Conditions')}</a>
                      </li>
                      <li>
                        <a href="/privacy">{t('Privacy Policy')}</a>
                      </li>
                      <li>
                        <a href="/volunteering">{t('Work with Us')}</a>
                      </li>
                    </ul>
                  </div>
                  <div className="col-sm-6 col-md-3">
                    <h3 className="font-brand-light">{t('Explore')}</h3>
                    <ul className="list-unstyled home-footer-pages font-brand-light">
                      <li>
                        <a href="/signup">{t('Join')}</a>
                      </li>
                      <li>
                        <a href="/signin">{t('Login')}</a>
                      </li>
                    </ul>
                  </div>
                  <div className="col-sm-6 col-md-3">
                    <h3 className="font-brand-light">
                      {t('Customer Service')}
                    </h3>
                    <ul className="list-unstyled home-footer-pages font-brand-light">
                      <li>
                        <a href="/faq">{t('FAQ')}</a>
                      </li>
                      <li>
                        <a href="/support">{t('Contact & support')}</a>
                      </li>
                    </ul>
                  </div>
                </>
              }
            />
          </div>

          <div className="row text-center">
            <hr className="hr-white hr-xs" />
            {/* Registered office. Kept here, always visible, since it moved
                out of the Customer Service column when that became a
                CMS-managed footer section. */}
            <small className="font-brand-light">
              Block C, 77 Sir John Rogerson&#39;s Quay, Grand Canal Dock,
              Dublin, D02 NP08, Ireland.
            </small>
            {/*
            {!isNativeMobileApp && (
              <div className="home-apps">
                <a
                  href="https://play.google.com/store/apps/details?id=org.trustroots.trapp"
                  rel="noopener"
                  className="btn btn-lg btn-default"
                >
                  <i className="icon-android"></i>
                  {t('Play Store')}
                </a>
                <a
                  href="https://apps.apple.com/nl/app/trustroots-community-app/id6447916847"
                  rel="noopener"
                  className="btn btn-lg btn-default"
                >
                  <i className="icon-apple"></i>
                  {t('App Store')}
                </a>
              </div>
            )}
            <ul className="list-inline home-footer-some">
              <li>
                <Tooltip id="facebook-tooltip" tooltip="Facebook">
                  <a
                    href="https://www.facebook.com/trustroots.org"
                    aria-label={t('Trustroots at Facebook')}
                  >
                    <i className="icon-facebook icon-lg"></i>
                  </a>
                </Tooltip>
              </li>
              <li>
                <Tooltip id="facebook-tooltip" tooltip="Twitter">
                  <a
                    href="https://twitter.com/trustroots"
                    aria-label={t('Trustroots at Twitter')}
                  >
                    <i className="icon-twitter icon-lg"></i>
                  </a>
                </Tooltip>
              </li>
              <li>
                <Tooltip id="instagram-tooltip" tooltip="Instagram">
                  <a
                    href="https://www.instagram.com/trustroots/"
                    aria-label={t('Trustroots at Instagram')}
                  >
                    <i className="icon-instagram icon-lg"></i>
                  </a>
                </Tooltip>
              </li>
              <li>
                <Tooltip id="github-tooltip" tooltip="GitHub">
                  <a
                    href="https://github.com/Trustroots/trustroots"
                    aria-label={t('Trustroots at GitHub')}
                  >
                    <i className="icon-github icon-lg"></i>
                  </a>
                </Tooltip>
              </li>
            </ul>
            */}

            <BoardCredits photoCredits={photoCredits} />
          </div>
          {/* .row */}
        </div>
        {/* /.container */}
      </Board>
    </>
  );
}

Home.propTypes = {
  user: userType,
  isNativeMobileApp: PropTypes.bool,
  photoCredits: PropTypes.object,
};
